import { Box, Divider, MenuItem, TextField } from '@mui/material';
import { ChangeEvent } from 'react';
import { QuestionType } from '@act/shared/types';
import OptionCreator from './options/OptionCreator';
import { SurveyV2 } from '@act/shared/data-transfer-objects';

interface QuestionSettingsPanelProps {
  question: SurveyV2.CreateQuestionJSONDto;
  questionCount: number;
  isActionSurvey: boolean;
  onQuestionTypeChange: (questionType: QuestionType) => void;
  onDateOffsetChange: (
    e: ChangeEvent<HTMLInputElement>,
    offsetType: 'minDateOffset' | 'maxDateOffset'
  ) => void;
  onOptionsChange: (options: SurveyV2.CreateOptionJSONDto[]) => void;
  formErrors?: any; // Form validation errors from React Hook Form
  questionIndex?: number; // Index of this question in the questions array
}

const QuestionSettingsPanel = ({
  question,
  questionCount,
  isActionSurvey,
  onQuestionTypeChange,
  onDateOffsetChange,
  onOptionsChange,
  formErrors,
  questionIndex,
}: QuestionSettingsPanelProps) => {
  function handleQuestionTypeChange(e: ChangeEvent<HTMLInputElement>): void {
    onQuestionTypeChange(e.target.value as QuestionType);
  }

  const questionRequiresOptions =
    question.type === 'checkbox' ||
    question.type === 'radio' ||
    question.type === 'select';

  const { maxDateOffset, minDateOffset } =
    question?.validationConfiguration || {};

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        label="Question type"
        value={question.type}
        onChange={handleQuestionTypeChange}
        select
        fullWidth
      >
        <MenuItem value="checkbox">Checkbox</MenuItem>
        <MenuItem value="date">Date</MenuItem>
        <MenuItem value="radio">Radio Group</MenuItem>
        <MenuItem value="select">Select</MenuItem>
        <MenuItem value="text">Text Field</MenuItem>
      </TextField>

      {question.type === 'date' && (
        <Box sx={{ display: 'flex', gap: '32px', marginTop: '16px' }}>
          <TextField
            label="Min date offset"
            type="number"
            value={minDateOffset !== undefined ? minDateOffset : ''}
            onChange={e =>
              onDateOffsetChange(
                e as ChangeEvent<HTMLInputElement>,
                'minDateOffset'
              )
            }
          />
          <TextField
            label="Max date offset"
            type="number"
            value={maxDateOffset !== undefined ? maxDateOffset : ''}
            onChange={e =>
              onDateOffsetChange(
                e as ChangeEvent<HTMLInputElement>,
                'maxDateOffset'
              )
            }
          />
        </Box>
      )}

      {question.type && questionRequiresOptions && (
        <>
          <Divider
            sx={{
              margin: '16px 8px',
              opacity: 1,
              '&::before, &::after': {
                borderTopWidth: '2px',
              },
            }}
          >
            Options
          </Divider>

          <Box sx={{ margingTop: '16px' }}>
            <OptionCreator
              questionKey={question.key}
              options={question.options || []}
              questionCount={questionCount}
              isActionSurvey={isActionSurvey}
              onOptionsChange={onOptionsChange}
              formErrors={formErrors}
              questionIndex={questionIndex}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default QuestionSettingsPanel;
